/**
 * Which end of the run a wild goes on, when it could go on either. The player decides —
 * it changes which real cards can follow it. Takes the place of the piles while open,
 * so it never covers the hand. scope.md §3.4 and §8.2.
 */

import type { Card, Meld, RoomRules } from '@garbage/shared';
import { extendMeld } from '@garbage/engine';
import { useT } from '../i18n.js';

type Stands = Meld['jokers'][number];

const GLYPH: Record<string, string> = { spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' };

/** Would this declaration put the wild below the run's first card? */
export function isLowEnd(meld: Meld, joker: Card, stands: Stands, rules: RoomRules): boolean {
  return extendMeld(meld, joker, rules, stands)?.cards[0]?.id === joker.id;
}

export function JokerEndChooser({
  meld, joker, options, rules, onPick, onCancel,
}: {
  meld: Meld;
  joker: Card;
  options: Stands[];
  rules: RoomRules;
  onPick: (stands: Stands) => void;
  onCancel: () => void;
}) {
  const t = useT();
  // Low end first, so the buttons read the same way round as the run.
  const ordered = options.slice().sort((a, b) =>
    Number(isLowEnd(meld, joker, b, rules)) - Number(isLowEnd(meld, joker, a, rules)));

  return (
    <div className="flex flex-col gap-2 px-3 pb-1" data-testid="joker-end-chooser">
      <p className="text-center text-sm font-bold text-violet-300">{t('jokerEnd.question')}</p>
      <div className="flex gap-2">
        {ordered.map((stands) => {
          const low = isLowEnd(meld, joker, stands, rules);
          return (
            <button
              key={`${stands.rank}-${stands.suit ?? ''}`}
              type="button"
              data-testid={`joker-end-${low ? 'low' : 'high'}`}
              onClick={() => onPick(stands)}
              className="flex-1 rounded-2xl bg-violet-600 py-2 font-black text-white active:scale-95"
            >
              {low ? t('jokerEnd.low') : t('jokerEnd.high')}
              <span className="block text-xs font-bold text-white/80">
                {t('jokerEnd.as', { card: `${stands.rank}${stands.suit ? GLYPH[stands.suit] : ''}` })}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        data-testid="joker-end-cancel"
        onClick={onCancel}
        className="rounded-xl bg-white/10 text-sm font-bold text-bone/80"
      >
        {t('common.cancel')}
      </button>
    </div>
  );
}
